fx.ns("shared", class BookMain extends shared.BaseMain {

	constructor() {
		super(new repositories.BookRepository());

		this.model.iaEnabled = this.ia;
		this.model.convertPicture = this.is3d ? "3d" : "";
	}

	// if url is /create/magic we create a magic album
	get ia() {
		return fx.browser.urlPathParts[1] == "magic";
	}

	// if url is /create/3D we create a 3d album
	get is3d() {
		return fx.browser.urlPathParts[1] == "3d";
	}

	// if url is /create/thema/[themeid] we create an album from a theme : the product page
	// carries the theme but no shape (the mobile app passes the theme as ?theme_id=)
	get isThema() {
		let product = window.Flexilivre && window.Flexilivre.product;

		return fx.browser.urlPathParts[1] == "thema" || !!(product && product.theme_id && !product.shape);
	}

	onLeave(e) {
		// if creating magic book, no need to stop user from leaving
		if (this.ia) return;

		super.onLeave(e);
	}

	onDataEvent(e) {
		// every photo added to the album (files, Google, phone) is analysed in the background
		// (quality, faces) : the automatic creation then reuses that instead of analysing again
		if (e.type == "creation-created" || e.type == "creation-loaded") {
			fx.service("PhotoCurator").watch();
			this._watchPhotos();
		}

		// a Google import validated on its import screen ("Importer", or an early import that never
		// sees the end of the extraction) : the customer may have stayed there longer than the offer
		// armed at the end of the extraction waits for the editor
		if (e.type == fx.service("GoogleService").importedEvent)
			this._onPhotosExtracted();

		// an album built by the automatic creation, opened again : the covers, backgrounds and
		// stickers of the category it was detected as
		if (e.type == "creation-loaded" && this.model.restoreAlbumCategory)
			this.model.restoreAlbumCategory();

		// a collection product : its covers naming where the photos were taken come first
		if (e.type == "creation-loaded" && this.model.rankCoversByPlace)
			this.model.rankCoversByPlace();

		super.onDataEvent(e);
	}

	/***************** Proposing the automatic album *****************/

	// photos enough for the placement to be worth proposing (the magic creation asks for as many)
	get MAGIC_MIN_PHOTOS() {
		return 24;
	}

	// how long the proposal waits for the editor : the import ends on a different screen depending
	// on the application, and the upload popup of the desktop closes on its own once it is done
	get MAGIC_MAX_TRIES() {
		return 30;
	}

	/**
	 * Whether the album is offered as soon as the customer has picked their photos rather than once
	 * every one of them is extracted. The mobile application says yes : extracting a whole import on
	 * a phone takes long enough that the offer would come after the customer has moved on, and the
	 * panel knows how to wait for the photos by itself (BaseStep.whenPhotosReady).
	 */
	get proposesMagicOnImport() {
		return false;
	}

	// the photos the customer picked, wherever they come from
	_watchPhotos() {
		if (this._magicWatching)
			return;

		this._magicWatching = true;

		this.on("photo-extracted-finished", this._onPhotosExtracted, fx.service("MediasUploader"));
		this.on("photo-extracted-finished", this._onPhotosExtracted, fx.service("GoogleService"));

		if (this.proposesMagicOnImport)
			this.on("files-selected", this._onPhotosImporting, fx.service("MediasUploader"));
	}

	/**
	 * Every photo of the import is there : the automatic album is proposed, once, as the
	 * MagicAlbumPanel confirmation, the screen the editor opens on "place-photos-chronologically".
	 */
	_onPhotosExtracted() {
		if (!this._canProposeMagic())
			return;

		if (this.model.photos.active.length < this.MAGIC_MIN_PHOTOS)
			return;

		this._offerMagicAlbum();
	}

	/**
	 * The import has just started : the album holds none of the picked files yet (selectedFiles
	 * raises this before it initialises them), so they are counted on top of the photos already
	 * there — a selection made in several goes reaches the minimum too. The panel opens on its
	 * waiting animation and only asks for the titles once the extraction is over.
	 */
	_onPhotosImporting(e) {
		if (!this._canProposeMagic())
			return;

		let picked = e && e.detail ? e.detail.length || 0 : 0;

		if (this.model.photos.active.length + picked < this.MAGIC_MIN_PHOTOS)
			return;

		this._offerMagicAlbum();
	}

	// the offer is made once, and not over an album that is already the result of a placement
	_canProposeMagic() {
		// the magic creation places the photos by itself, its panel is already a step of the Main
		if (this._magicProposed || fx.get("mode") == "creating-ia")
			return false;

		// an album already made automatically (the magic creation, or an earlier placement) : the
		// customer knows the feature, and their album is already the result of it
		if (this.model.creation && Number(this.model.creation.ia_project) > 0)
			return false;

		return true;
	}

	// armed again while already waiting (the end of the extraction, then the import validated) :
	// the wait starts over, without a second one running alongside
	_offerMagicAlbum() {
		this._magicTries = 0;

		if (this._magicWaiting)
			return;

		this._magicWaiting = true;
		this.delay(this._proposeMagicAlbum, 600);
	}

	// a PopupPanel closes without telling the Main, which then keeps it as its current popup : one
	// that is no longer on screen (destroyed, so without node) does not count
	get _popupShown() {
		let popup = this.currentPopup;

		return !!(popup && popup.node);
	}

	// the panel takes the whole screen : it waits for the editor rather than taking the place of
	// another screen, and for the popups of the import to be gone
	_proposeMagicAlbum() {
		this._magicWaiting = false;

		if (this._magicProposed)
			return;

		// armed at the end of an import, but "Quitter" can have deleted those photos since
		if (this.model.photos.active.length < this.MAGIC_MIN_PHOTOS)
			return;

		if (this.currentView != "BookEditor" || this._popupShown) {
			if (++this._magicTries < this.MAGIC_MAX_TRIES) {
				this._magicWaiting = true;
				this.delay(this._proposeMagicAlbum, 1000);
			}

			return;
		}

		this._magicProposed = true;
		this.model.event("place-photos-chronologically");
	}


	// The shape is asked before the editor opens when the product page did not fix it : an
	// album created from a theme, a stapled notebook (option 6) or a hard layflat album
	// (option 9). choosedCover carries either the option id (product page) or the editor
	// code (url).
	get shapeAskedAtCreation() {
		if (this.isThema)
			return true;

		let cover = String(this.model.choosedCover || "").toLowerCase();

		return cover == "6" || cover == models.ProductBook.BIND || cover == "9" || cover == models.ProductBook.LAYFLAT;
	}

	/***************** Format / cover asked at order time *****************/

	// A book opens straight on the editor : the format and the cover screens are not
	// part of the creation steps any more, the order button asks for them instead.
	markCreationChoicesPending() {
		// both are always pending on a new book : what the product page carries is a default,
		// the user himself never picked anything. The repository keeps them in the report json
		// of the creation, and the screens clear them as soon as the user picks something,
		// wherever he does it from (info panel included)
		this.model.formatChoicePending = true;
		this.model.coverChoicePending = true;
	}

	// Displays the screens skipped at creation, on the last step before the cart (so after
	// the preview on desktop). Returns true when there is something to ask : the order is
	// then resumed by endCreationChoices() once every screen is done.
	startCreationChoices() {
		if (this._creationChoicesAsked) // already gone through them in this session
			return false;

		this._creationChoices = [];

		// format : never picked, a choice to make as soon as two formats are possible (a stapled
		// album stays in its small formats). Already picked, an invitation to go larger.
		let stapled = this.model.creation.isStapledProduct || this.model.creation.isBind;
		let formatToPick = !stapled && this.model.possibleFormats().length >= 2;

		// the product fixed the format, and at the largest one : nothing bigger to offer
		let product = this.model.creation.product;
		if (product && Number(product.format_id) > 0 && product.format_id == this.model.creation.formatid && !this.model.largerFormatAvailable)
			formatToPick = false;

		// an album with the stapled cover is never offered another format, whether it picked
		// its format or not (the stapled cover only exists in the small formats)
		let formatScreen = this.model.formatChoicePending ? formatToPick : this.model.largerFormatAvailable;

		if (formatScreen && !this.model.creation.isBind)
			this._creationChoices.push("SizeAlbum");

		// cover : never picked, a choice to make as soon as two covers are possible - unless the
		// album already has the layflat one, the best there is. Already picked, an invitation
		// to a pricier one.
		let coverToPick = !this.model.creation.isLayFlat && this.model.creation.coverChoicesToPick().length >= 2;

		// a stapled notebook is only worth the screen when a soft or a hard cover can take it
		if (this.model.creation.isBind)
			coverToPick = coverToPick && this.model.creation.hasPossibleCover(models.ProductBook.SOFT, models.ProductBook.HARD);

		if (this.model.coverChoicePending ? coverToPick : this.model.creation.coverUpgradeChoices().length > 0)
			this._creationChoices.push("CoverAlbum");

		if (this._creationChoices.length == 0)
			return false;

		this._creationChoiceIndex = -1;
		this._creationChoicesReturnView = this.currentView; // preview, check book, editor...
		this.model.preOrderChoices = true; // tells the screens they drive these steps

		this.nextCreationChoice();

		return true;
	}

	// A choice was made, or the screen was skipped : display the following one.
	nextCreationChoice() {
		if (!this._creationChoices)
			return;

		this._creationChoiceIndex++;

		let next = this._creationChoices[this._creationChoiceIndex];

		if (next)
			this.switchTo(next);
		else
			this.endCreationChoices(true);
	}

	// Back arrow : previous screen, or back to the editor when we are on the first one
	previousCreationChoice() {
		if (!this._creationChoices)
			return;

		this._creationChoiceIndex--;

		let previous = this._creationChoices[this._creationChoiceIndex];

		if (previous)
			this.switchTo(previous);
		else
			this.endCreationChoices(false);
	}

	// order : true when every screen was gone through, false when the user went back out
	endCreationChoices(order) {
		this._creationChoices = null;
		this.model.preOrderChoices = false;

		if (order) {
			// a skipped screen stays pending on the creation, so it is asked again the next
			// time the album is opened - but not a second time in this session
			this._creationChoicesAsked = true;

			this.model.event("order-confirmed");
		}
		else
			// back arrow on the first screen : the user returns where he was ordering from -
			// the editor when that was the splash of an album opened for a last check
			if (this._creationChoicesReturnView && this._creationChoicesReturnView != "BookEditor" && this._creationChoicesReturnView != "Splash")
				this.switchTo(this._creationChoicesReturnView);
			else
				this.model.event("show-editor");
	}

});